/** Reproducción auditiva ligera — Web Audio API, sin MP3. */

export async function playFrequencySequence(
  frequencies: number[],
  opts?: { gapMs?: number; durationMs?: number; wave?: OscillatorType },
): Promise<void> {
  if (typeof window === 'undefined' || !frequencies.length) return;
  const ctx = new AudioContext();
  const gap = opts?.gapMs ?? 380;
  const duration = opts?.durationMs ?? 320;
  const wave = opts?.wave ?? 'triangle';

  for (let i = 0; i < frequencies.length; i += 1) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = wave;
    osc.frequency.value = frequencies[i];
    gain.gain.value = 0.18;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const start = ctx.currentTime + (i * gap) / 1000;
    osc.start(start);
    osc.stop(start + duration / 1000);
  }

  await new Promise((r) => window.setTimeout(r, frequencies.length * gap + duration));
  await ctx.close();
}

/** Ritmo con clicks proporcionales a la duración de cada token. */
export async function playPatternTokens(
  tokens: string[],
  bpm = 72,
  beatValues?: number[],
): Promise<void> {
  if (typeof window === 'undefined' || !tokens.length) return;
  const ctx = new AudioContext();
  const beatMs = 60_000 / bpm;
  let elapsed = 0;

  for (let i = 0; i < tokens.length; i += 1) {
    const beats = beatValues?.[i] ?? tokenBeatsFromSymbol(tokens[i]);
    const durationMs = Math.max(beatMs * beats, beatMs * 0.25);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = i === 0 ? 880 : 660;
    gain.gain.value = 0.14;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const start = ctx.currentTime + elapsed / 1000;
    osc.start(start);
    osc.stop(start + 0.07);
    elapsed += durationMs;
  }

  await new Promise((r) => window.setTimeout(r, elapsed + 120));
  await ctx.close();
}

function tokenBeatsFromSymbol(token: string): number {
  switch (token) {
    case 'W':
      return 4;
    case 'H':
      return 2;
    case 'Q':
    case 'R_Q':
      return 1;
    case 'E':
      return 0.5;
    case 'S':
      return 0.25;
    default:
      return 1;
  }
}

/** Ritmo con clicks de metrónomo según duración de token (negra = 1 beat). */
export async function playRhythmClicks(beatCount: number, bpm = 72): Promise<void> {
  if (typeof window === 'undefined' || beatCount < 1) return;
  const ctx = new AudioContext();
  const interval = 60_000 / bpm;
  for (let i = 0; i < beatCount; i += 1) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = i === 0 ? 880 : 660;
    gain.gain.value = 0.12;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const t = ctx.currentTime + (i * interval) / 1000;
    osc.start(t);
    osc.stop(t + 0.06);
  }
  await new Promise((r) => window.setTimeout(r, beatCount * interval + 80));
  await ctx.close();
}

/** Frecuencias estándar para notas en español (octava 4–5). */
export const NOTE_FREQ: Record<string, number> = {
  Do: 261.63,
  Re: 293.66,
  Mi: 329.63,
  Fa: 349.23,
  Sol: 392.0,
  La: 440.0,
  Si: 493.88,
};

export function frequenciesFromNoteNames(names: string[]): number[] {
  return names.map((n) => {
    const key = String(n).trim().split(/[\s-]/)[0];
    const normalized = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
    return NOTE_FREQ[normalized] ?? 440;
  });
}
